import type { ExportFormat } from '@tempo-io/roadmap-dto';

export const loopVisualizationQueryKeys = {
  items: {
    partial: () => ['loop-visualization', 'items'] as const,
    list: ({ filterKey }: { filterKey?: string } = {}) =>
      ['loop-visualization', 'items', { filterKey }] as const,
  },
  loopItem: {
    detail: (itemId: number) => ['loop', 'item', itemId] as const,
  },
  itemsViewLayout: {
    detail: (loopId: number) => ['loop', loopId, 'items', 'layout'] as const,
  },
  viewItems: {
    /** Partial key to invalidate all view items for a view */
    partial: (viewId: number) => ['view', viewId] as const,
    partialViewItems: (viewId: number) => ['view', viewId, 'items'] as const,
    detail: (viewId: number, visualizationId?: number) =>
      visualizationId === undefined
        ? (['view', viewId, 'items'] as const)
        : (['view', viewId, 'items', { visualizationId }] as const),
    treeSet: (viewId: number, filterKey?: string) =>
      ['view', viewId, 'items', 'tree-set', { filterKey }] as const,
  },
  snapshot: {
    all: (loopId: number, viewId: number) => ['loop', loopId, 'view', viewId, 'snapshot'] as const,
    items: (loopId: number, viewId: number) =>
      ['loop', loopId, 'view', viewId, 'snapshot', 'items'] as const,
    schedules: (loopId: number, viewId: number) =>
      ['loop', loopId, 'view', viewId, 'snapshot', 'schedules'] as const,
  },
  visualization: {
    all: (viewId: number) => ['visualization', 'list', viewId] as const,
    detail: (viewId: number, visualizationId: number) =>
      ['visualization', 'detail', viewId, visualizationId] as const,
    byType: (viewId: number, type: string) => ['visualization', 'byType', viewId, type] as const,
    loopList: (loopId: number) => ['visualization', 'loop-list', loopId] as const,
  },
  dashboard: {
    all: () => ['dashboard'] as const,
    list: (loopId: number) => ['dashboard', 'list', loopId] as const,
    detail: (dashboardId: number) => ['dashboard', 'detail', dashboardId] as const,
  },
  swimlaneLayout: {
    detail: (viewId: number, visualizationId: number) =>
      ['swimlane-layout', viewId, visualizationId] as const,
    headerConfig: (
      viewId: number,
      visualizationId: number,
      headerFieldId: string,
      parentFieldId?: string | null,
      parentHeaderId?: string | null,
    ) =>
      [
        'swimlane-layout',
        'header-config',
        viewId,
        visualizationId,
        headerFieldId,
        parentFieldId ?? null,
        parentHeaderId ?? null,
      ] as const,
  },
  tableLayout: {
    detail: (viewId: number, visualizationId: number) =>
      ['table-layout', viewId, visualizationId] as const,
  },
  itemRanks: {
    detail: (viewId: number, visualizationId: number) =>
      ['item-ranks', viewId, visualizationId] as const,
  },
  timelineLayout: {
    detail: (viewId: number, visualizationId: number) =>
      ['timeline-layout', viewId, visualizationId] as const,
    headerConfig: ({
      viewId,
      visualizationId,
      headerFieldId,
      parentFieldId,
      parentHeaderId,
    }: {
      viewId: number;
      visualizationId: number;
      headerFieldId: string;
      parentFieldId?: string | null;
      parentHeaderId?: string | null;
    }) =>
      [
        'timeline-layout',
        'header-config',
        viewId,
        visualizationId,
        headerFieldId,
        parentFieldId,
        parentHeaderId,
      ] as const,
  },
  publishedVisualization: {
    detail: (viewId: number, visualizationId: number) =>
      ['published-visualization', viewId, visualizationId] as const,
  },
  exportStatus: {
    detail: (viewId: number, visualizationId: number, format: ExportFormat) =>
      ['export-status', viewId, visualizationId, format] as const,
  },
};
